import React, { useState, useEffect } from 'react';
import { testimonialService, Testimonial } from '../../services/testimonialService';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import LoadingSpinner from '../ui/LoadingSpinner';

export const TestimonialManager: React.FC = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    clientName: '',
    projectName: '',
    comment: '',
    rating: 5,
    initials: '',
    active: true
  });

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      setIsLoading(true);
      const data = await testimonialService.getAllTestimonials();
      setTestimonials(data);
      setError(null);
    } catch (err) {
      setError('Failed to load testimonials');
      console.error('Error loading testimonials:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const generateInitials = (name: string) => {
    return name
      .split(/\s+/)
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    // If the client name is being changed, auto-generate initials
    if (name === 'clientName') {
      const newInitials = generateInitials(value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        initials: newInitials
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' 
          ? (e.target as HTMLInputElement).checked 
          : name === 'rating' ? parseInt(value, 10) : value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSaving(true);
      setError(null);
      
      if (editingId) {
        // Update existing testimonial
        await testimonialService.updateTestimonial(editingId, formData);
      } else {
        // Add new testimonial
        // Calculate next order number
        const nextOrder = testimonials.length > 0 
          ? Math.max(...testimonials.map(t => t.order)) + 1 
          : 0;
        
        await testimonialService.addTestimonial({
          ...formData,
          order: nextOrder
        });
      }
      
      await loadTestimonials();
      resetForm();
    } catch (err) {
      setError('Failed to save testimonial');
      console.error('Error saving testimonial:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (testimonial: Testimonial) => {
    setEditingId(testimonial.id);
    setFormData({
      clientName: testimonial.clientName,
      projectName: testimonial.projectName,
      comment: testimonial.comment,
      rating: testimonial.rating,
      initials: testimonial.initials,
      active: testimonial.active
    });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) {
      return;
    }
    
    try {
      setIsSaving(true);
      await testimonialService.deleteTestimonial(id);
      await loadTestimonials();
    } catch (err) {
      setError('Failed to delete testimonial');
      console.error('Error deleting testimonial:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      clientName: '',
      projectName: '',
      comment: '',
      rating: 5,
      initials: '',
      active: true
    });
  };

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source } = result;
    
    // If dropped outside a droppable area or no change in position
    if (!destination || destination.index === source.index) {
      return;
    }
    
    try {
      setIsSaving(true);
      
      // Reorder the items in our state
      const reorderedTestimonials = Array.from(testimonials);
      const [movedItem] = reorderedTestimonials.splice(source.index, 1);
      reorderedTestimonials.splice(destination.index, 0, movedItem);
      
      // Update our UI optimistically
      setTestimonials(reorderedTestimonials);
      
      // Update the order in the database
      const orderedIds = reorderedTestimonials.map(testimonial => testimonial.id);
      await testimonialService.updateTestimonialOrder(orderedIds);
    } catch (err) {
      // Reload from the database if there's an error
      console.error('Error updating order:', err);
      await loadTestimonials();
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">Client Testimonials</h2>
        <div className="text-sm text-gray-500">
          {testimonials.length} {testimonials.length === 1 ? 'Testimonial' : 'Testimonials'} • 
          {testimonials.filter(t => t.active).length} Active
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Testimonial Form */}
      <form onSubmit={handleSubmit} className="mb-8 border rounded-lg p-4">
        <h3 className="text-lg font-medium mb-4">
          {editingId ? 'Edit Testimonial' : 'Add New Testimonial'}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="clientName">
              Client Name
            </label>
            <input
              type="text"
              id="clientName"
              name="clientName"
              value={formData.clientName}
              onChange={handleInputChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="projectName">
              Project Name
            </label>
            <input
              type="text"
              id="projectName"
              name="projectName"
              value={formData.projectName}
              onChange={handleInputChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
              required
            />
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="comment">
            Testimonial
          </label>
          <textarea
            id="comment"
            name="comment"
            value={formData.comment}
            onChange={handleInputChange}
            rows={3}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
            required
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="rating">
              Rating (1-5 Stars)
            </label>
            <select
              id="rating"
              name="rating"
              value={formData.rating}
              onChange={handleInputChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
            >
              <option value={1}>1 Star</option>
              <option value={2}>2 Stars</option>
              <option value={3}>3 Stars</option>
              <option value={4}>4 Stars</option>
              <option value={5}>5 Stars</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="initials">
              Initials (Auto-generated)
            </label>
            <input
              type="text"
              id="initials"
              name="initials"
              value={formData.initials}
              onChange={handleInputChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500 bg-gray-50"
              maxLength={2}
              readOnly
            />
          </div>
        </div>
        
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="active"
            name="active"
            checked={formData.active}
            onChange={handleInputChange}
            className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-gray-300 rounded"
          />
          <label htmlFor="active" className="ml-2 block text-sm text-gray-700">
            Active
          </label>
        </div>
        
        <div className="flex justify-end space-x-2">
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
              disabled={isSaving}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : editingId ? 'Update Testimonial' : 'Add Testimonial'}
          </button>
        </div>
      </form>

      {/* Testimonial List */}
      <h3 className="text-lg font-medium mb-4">Manage Testimonials</h3>
      <p className="text-sm text-gray-500 mb-4">
        Drag and drop to reorder testimonials. Testimonials are displayed in the order shown below.
      </p>
      
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="testimonials">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="space-y-3"
            >
              {testimonials.length === 0 ? (
                <div className="text-center p-8 border border-dashed rounded-md">
                  <p className="text-gray-500">No testimonials added yet. Add your first testimonial above.</p>
                </div>
              ) : (
                testimonials.map((testimonial, index) => (
                  <Draggable key={testimonial.id} draggableId={testimonial.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`border rounded-md p-4 ${
                          snapshot.isDragging ? 'bg-accent-50' : 'bg-white'
                        } ${!testimonial.active ? 'opacity-60' : ''}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-4">
                            <div {...provided.dragHandleProps} className="cursor-move pt-1">
                              <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zM13 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
                              </svg>
                            </div>
                            
                            <div className="w-10 flex-shrink-0">
                              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-bold">
                                {testimonial.initials}
                              </div>
                            </div>
                            
                            <div className="flex-grow">
                              <div className="flex justify-between">
                                <h4 className="text-lg font-medium text-primary-900">
                                  {testimonial.clientName}
                                </h4>
                                <div className="flex">
                                  {[...Array(5)].map((_, i) => (
                                    <svg 
                                      key={i} 
                                      className={`w-4 h-4 ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`} 
                                      fill="currentColor" 
                                      viewBox="0 0 20 20"
                                    >
                                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                  ))}
                                </div>
                              </div>
                              <p className="text-sm text-primary-500 mt-1">
                                {testimonial.projectName}
                              </p>
                              <p className="text-sm text-gray-600 mt-2 line-clamp-2 italic">
                                "{testimonial.comment}"
                              </p>
                              <div className="mt-2 flex items-center text-xs text-gray-500">
                                <span className={`px-2 py-1 rounded-full ${
                                  testimonial.active 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {testimonial.active ? 'Active' : 'Inactive'}
                                </span>
                                <span className="ml-2">Order: {testimonial.order}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex space-x-2">
                            <button
                              type="button"
                              onClick={() => handleEdit(testimonial)}
                              className="text-gray-400 hover:text-primary-600"
                              aria-label="Edit"
                            >
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(testimonial.id)}
                              className="text-gray-400 hover:text-red-600"
                              aria-label="Delete"
                            >
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
}; 